import React, { useState } from 'react';
import styled from 'styled-components';
import { theme } from '@theme';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button, Input, Card } from '@/components/common';
import { FiSave, FiSettings as FiSettingsIcon, FiGlobe, FiZap } from 'react-icons/fi';

const PageHeader = styled.div`
  margin-bottom: ${theme.spacing.xl};
`;

const PageTitle = styled.h1`
  font-size: ${theme.typography.sizes['3xl']};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.sm} 0;
`;

const PageSubtitle = styled.p`
  font-size: ${theme.typography.sizes.base};
  color: ${theme.colors.text}80;
  margin: 0;
`;

const SettingsGrid = styled.div`
  display: grid;
  gap: ${theme.spacing.xl};
`;

const SettingSection = styled(Card)`
  padding: ${theme.spacing.xl};
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
  padding-bottom: ${theme.spacing.lg};
  border-bottom: 1px solid ${theme.colors.surface};
`;

const SectionIcon = styled.div<{ color: string }>`
  width: 48px;
  height: 48px;
  border-radius: ${theme.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.color}20;
  color: ${props => props.color};
  font-size: 24px;
`;

const SectionTitle = styled.h2`
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin: 0;
`;

const FormGroup = styled.div`
  margin-bottom: ${theme.spacing.lg};

  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: block;
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.medium};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.sm};
`;

const Description = styled.p`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}60;
  margin: ${theme.spacing.xs} 0 0 0;
`;

const ColorPicker = styled.input`
  width: 100px;
  height: 40px;
  border: 1px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  padding: 0;

  &::-webkit-color-swatch-wrapper {
    padding: 2px;
  }

  &::-webkit-color-swatch {
    border: none;
    border-radius: ${theme.borderRadius.sm};
  }
`;

const ColorGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
`;

const ColorLabel = styled.div`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text};
  min-width: 120px;
`;

const Select = styled.select`
  width: 100%;
  padding: ${theme.spacing.md};
  border: 1px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.base};
  color: ${theme.colors.text};
  background: ${theme.colors.white};
  transition: border-color ${theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: ${theme.spacing.md};
  border: 1px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.base};
  font-family: ${theme.typography.fontFamily};
  color: ${theme.colors.text};
  background: ${theme.colors.white};
  resize: vertical;
  transition: border-color ${theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const SaveButton = styled(Button)`
  margin-top: ${theme.spacing.lg};
`;

const SuccessMessage = styled.div`
  background: ${theme.colors.success}10;
  color: ${theme.colors.success};
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  margin-bottom: ${theme.spacing.lg};
  border-left: 4px solid ${theme.colors.success};
`;

export const OrganizationSettings: React.FC = () => {
  const [saved, setSaved] = useState(false);
  const [settings, setSettings] = useState({
    organization_name: 'Mon Organisation',
    website: 'https://example.com',
    primary_color: '#6A8A82',
    secondary_color: '#B87333',
    accent_color: '#F4A460',
    language: 'fr',
    timezone: 'Europe/Paris',
    webhook_url: '',
    webhook_events: ['application.created', 'scoring.completed'],
    email_notifications: true,
    sms_notifications: false,
  });

  const handleSave = () => {
    // TODO: Implement save settings logic
    console.log('Saving settings:', settings);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <DashboardLayout>
      <PageHeader>
        <PageTitle>Paramètres</PageTitle>
        <PageSubtitle>Personnalisez votre organisation</PageSubtitle>
      </PageHeader>

      {saved && <SuccessMessage>Paramètres sauvegardés avec succès !</SuccessMessage>}

      <SettingsGrid>
        {/* General Settings */}
        <SettingSection>
          <SectionHeader>
            <SectionIcon color={theme.colors.primary}>
              <FiSettingsIcon />
            </SectionIcon>
            <SectionTitle>Paramètres généraux</SectionTitle>
          </SectionHeader>

          <FormGroup>
            <Input
              type="text"
              label="Nom de l'organisation"
              value={settings.organization_name}
              onChange={e => setSettings({ ...settings, organization_name: e.target.value })}
              fullWidth
            />
            <Description>Le nom de votre organisation tel qu'il apparaîtra dans l'application</Description>
          </FormGroup>

          <FormGroup>
            <Input
              type="url"
              label="Site web"
              value={settings.website}
              onChange={e => setSettings({ ...settings, website: e.target.value })}
              fullWidth
            />
          </FormGroup>

          <FormGroup>
            <Label>Langue par défaut</Label>
            <Select
              value={settings.language}
              onChange={e => setSettings({ ...settings, language: e.target.value })}
            >
              <option value="fr">Français</option>
              <option value="en">English</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>Fuseau horaire</Label>
            <Select
              value={settings.timezone}
              onChange={e => setSettings({ ...settings, timezone: e.target.value })}
            >
              <option value="Europe/Paris">Europe/Paris (GMT+1)</option>
              <option value="Europe/London">Europe/London (GMT)</option>
              <option value="America/New_York">America/New_York (GMT-5)</option>
              <option value="America/Los_Angeles">America/Los_Angeles (GMT-8)</option>
            </Select>
          </FormGroup>
        </SettingSection>

        {/* Branding Settings */}
        <SettingSection>
          <SectionHeader>
            <SectionIcon color={theme.colors.accent}>
              <FiGlobe />
            </SectionIcon>
            <SectionTitle>Personnalisation visuelle</SectionTitle>
          </SectionHeader>

          <FormGroup>
            <Label>Couleurs de la marque</Label>
            <Description>Personnalisez les couleurs de votre interface</Description>
          </FormGroup>

          <FormGroup>
            <ColorGroup>
              <ColorLabel>Couleur principale:</ColorLabel>
              <ColorPicker
                type="color"
                value={settings.primary_color}
                onChange={e => setSettings({ ...settings, primary_color: e.target.value })}
              />
              <span style={{ fontSize: theme.typography.sizes.sm, color: theme.colors.text + '60' }}>
                {settings.primary_color}
              </span>
            </ColorGroup>
          </FormGroup>

          <FormGroup>
            <ColorGroup>
              <ColorLabel>Couleur secondaire:</ColorLabel>
              <ColorPicker
                type="color"
                value={settings.secondary_color}
                onChange={e => setSettings({ ...settings, secondary_color: e.target.value })}
              />
              <span style={{ fontSize: theme.typography.sizes.sm, color: theme.colors.text + '60' }}>
                {settings.secondary_color}
              </span>
            </ColorGroup>
          </FormGroup>

          <FormGroup>
            <ColorGroup>
              <ColorLabel>Couleur d'accent:</ColorLabel>
              <ColorPicker
                type="color"
                value={settings.accent_color}
                onChange={e => setSettings({ ...settings, accent_color: e.target.value })}
              />
              <span style={{ fontSize: theme.typography.sizes.sm, color: theme.colors.text + '60' }}>
                {settings.accent_color}
              </span>
            </ColorGroup>
          </FormGroup>
        </SettingSection>

        {/* Webhook Settings */}
        <SettingSection>
          <SectionHeader>
            <SectionIcon color={theme.colors.success}>
              <FiZap />
            </SectionIcon>
            <SectionTitle>Webhooks & Intégrations</SectionTitle>
          </SectionHeader>

          <FormGroup>
            <Input
              type="url"
              label="URL du Webhook"
              value={settings.webhook_url}
              onChange={e => setSettings({ ...settings, webhook_url: e.target.value })}
              placeholder="https://api.example.com/webhook"
              fullWidth
            />
            <Description>
              Recevez des notifications en temps réel sur les événements de votre organisation
            </Description>
          </FormGroup>

          <FormGroup>
            <Label>Événements à suivre</Label>
            <TextArea
              value={settings.webhook_events.join('\n')}
              onChange={e =>
                setSettings({ ...settings, webhook_events: e.target.value.split('\n') })
              }
              placeholder="application.created&#10;scoring.completed&#10;commission.decided"
            />
            <Description>Un événement par ligne</Description>
          </FormGroup>
        </SettingSection>
      </SettingsGrid>

      <SaveButton variant="primary" size="lg" icon={<FiSave />} onClick={handleSave}>
        Sauvegarder les paramètres
      </SaveButton>
    </DashboardLayout>
  );
};
